import { DialogContent, Modal } from "@mui/material"
import React, { useEffect, useRef } from "react"

import { Player } from "../../components/Player/Player"
import { usePlayer } from "../../components/Player/use-player"
import useSdk, { Client, ErrorLevel } from "../../hooks/use-sdk"
import { appStore } from "../../store/AppStore"
import { playerStore } from "../../store/PlayerStore"
import { maxSliderSize, version } from "../../utilities/constants"

const Screen = () => {
  const { isPreview } = playerStore((state) => state.navigationProps)

  const setIsPreview = playerStore((state) => state.setIsPreview)
  const trackIdState = playerStore((state) => state.trackId)
  const setAccessToken = playerStore((state) => state.setAccessToken)
  const videoRef = playerStore((state) => state.videoRef)
  const isPreviewState = playerStore((state) => state.isPreview)
  const { open } = playerStore((state) => state.navigationProps)
  const duration = playerStore((state) => state.duration)
  const content = playerStore((state) => state.content)
  const accessToken = appStore((state) => state.accessTokenState)
  const setMyStage = appStore((state) => state.setMyStage)
  const { pause } = usePlayer()
  const sdk = useSdk(ErrorLevel.NoError)
  const setSpinner = appStore((state) => state.setSpinner)
  const currentUser = appStore((state) => state.currentUser)
  const trackedProgress = useRef<{ id?: string; progress?: number }>({
    id: undefined,
    progress: undefined,
  })

  const reloadMyStage = async () => {
    if (!currentUser) return
    const result = await sdk.myStagePage({
      sliderPageSize: maxSliderSize,
      cursor: undefined,
    })
    setMyStage(result)
  }

  useEffect(() => {
    if (!open && trackedProgress.current.id) {
      reloadMyStage()
    } else trackedProgress.current = { id: undefined, progress: undefined }
  }, [open])

  useEffect(() => {
    setIsPreview(isPreview === "true")
    setAccessToken(accessToken)
  }, [isPreview, accessToken])

  useEffect(() => {
    let intervalId: NodeJS.Timer | undefined
    let checkIntervalId: NodeJS.Timer | undefined

    // checks if tracking interval should start
    const check = () => {
      if (!videoRef) return
      const currentTime = videoRef.currentTime
      if (currentTime >= 10 && !intervalId) {
        trackProgress()

        intervalId = setInterval(trackProgress, 10_000)
      } else if (currentTime < 10) {
        clearInterval(intervalId)
        intervalId = undefined
      }
    }

    const trackProgress = () => {
      if (!videoRef) return
      const currentTime = videoRef.currentTime
      if (!isPreviewState && currentTime >= 10) {
        sdk.recordPlaybackProgress({
          input: {
            client: Client.SmartTv,
            clientDevice: "SmartTV",
            clientTimestamp: Math.round(Date.now() / 1000),
            clientVersion: version,
            mark: Math.max(0, Math.round(currentTime)),
            containerId: content?.id ? content.id : "",
            contentId: trackIdState
              ? trackIdState
              : content?.id
                ? content.id
                : "",
            heartbeatRate: 10,
          },
        })
      }
      trackedProgress.current = {
        id: trackIdState,
        progress: currentTime / duration,
      }
    }
    const cleanUp = () => {
      clearInterval(intervalId)
      intervalId = undefined
      clearInterval(checkIntervalId)
      checkIntervalId = undefined
    }

    // start / stop check interval on player open
    if (open) {
      checkIntervalId = setInterval(check, 1_000)
    } else cleanUp()

    // Cleanup function
    return () => cleanUp()
  }, [trackIdState, isPreviewState, duration, open, videoRef, content])

  // ensure video is stopped on destruct
  useEffect(() => {
    setSpinner("out")
    return () => pause()
  }, [])

  return (
    <>
      <Player />
    </>
  )
}

export const PlayerScreen = () => {
  const { open } = playerStore((state) => state.navigationProps)

  return (
    // can't use the "loading_in" class because on Tizen (newer versions) the opacity overlay seems to kill the video
    <Modal
      keepMounted
      open={open}
      sx={{
        position: "fixed",
        zIndex: 1301,
        top: 0,
        left: 0,
        height: "100vh",
        width: "100vw",
        backgroundColor: "black",
        "& > *": { transition: "none !important" },
      }}
    >
      <DialogContent>
        <Screen />
      </DialogContent>
    </Modal>
  )
}
